import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
    Box, 
    Typography, 
    Container, 
    Paper, 
    TextField, 
    Autocomplete, 
    Button, 
    Grid, 
    CircularProgress, 
    Chip,
    Link as MuiLink,
    useMediaQuery,
    useTheme,
    Stack,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@mui/material';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import { LocationOn, Search, CalendarMonth, ArrowForward, Map as MapIcon, LocationOn as LocationOnIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import TopNav from './TopNav';
import Fonts from './Fonts';
import Footer from './Footer';
import { themeColors } from '../styles/theme';
import { searchCities, getSunnyPubsByCity, getSunnyPubsForEnhancedCity, getPubRaycasting } from '../API';
import debounce from 'lodash/debounce';
import ScrollIndicator from './ScrollIndicator';
import { PubObject, HeatmapData } from './FindPubs';
import PubCard from './PubCard';
import AdSense from './AdSense';
import { isCrawler } from '../utils/isCrawler';
import { calculateBeerMugRating } from '../utils/ratingCalculations';
import { useLocation } from '../contexts/LocationContext';
import { metresBetween } from '../utils/getDistance';
import { PrimaryButton } from './Home';
import pintManIllustration from '../img/pint_man_illustration.png';

interface City {
    city: string;
    verified_gardens: number;
}

interface CityLandingPageProps {
    cityOverride?: string;
    isEnhancedPage?: boolean;
    pubCount?: number;
    enhancedBlurb?: string;
    enhancedDisplayName?: string;
}

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: '24px',
    borderRadius: '16px',
    background: 'rgba(255, 255, 255, 0.9)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    border: `1px solid ${themeColors.orange}20`,
}));

const SearchButton = styled(Button)(({ theme }) => ({
    background: themeColors.orange,
    color: 'white',
    borderRadius: '28px',
    fontWeight: 600,
    padding: '10px 24px',
    textTransform: 'none',
    fontSize: '1rem',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
    '&:hover': {
        background: themeColors.mainYellow,
        boxShadow: '0 6px 15px rgba(0,0,0,0.15)',
        transform: 'translateY(-2px)'
    }
}));

const StyledHeading = styled(Typography)<{ component?: React.ElementType }>(({ theme }) => ({
    fontFamily: "ApfelGrotezkRegular",
    fontWeight: 700,
    color: themeColors.darkBackground,
}));

const CityLandingPage: React.FC<CityLandingPageProps> = ({ cityOverride, isEnhancedPage = false, pubCount = 10, enhancedBlurb, enhancedDisplayName }) => {
    const { encodedCity } = useParams<{ encodedCity: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isBot = useMemo(() => isCrawler(), []);
    const { searchLocation, hasLocationPermission, requestUserLocation, clearCustomSearchLocation } = useLocation();
    
    const [cityName, setCityName] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [sunniestPubs, setSunniestPubs] = useState<PubObject[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [citySuggestions, setCitySuggestions] = useState<City[]>([]);
    const [selectedCity, setSelectedCity] = useState<City | null>(null);
    const [totalPubsInCity, setTotalPubsInCity] = useState<number>(0);

    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [canonicalUrl, setCanonicalUrl] = useState<string>('');
    const [raycastLoading, setRaycastLoading] = useState<boolean>(false);
    const [raycastingPubIds, setRaycastingPubIds] = useState<Set<string>>(new Set());
    const [locationModalOpen, setLocationModalOpen] = useState<boolean>(false);
    
    // --- CHANGE: Combined loading state ---
    const isFullyLoading = loading || raycastLoading;

    const getDecodedCityName = useCallback((): string => {
        if (cityOverride) return cityOverride;
        if (!encodedCity) return '';
        try {
            return decodeURIComponent(encodedCity).replace(/<[^>]*>/g, '');
        } catch (e) {
            console.error('Error decoding city name:', e);
            return encodedCity.replace(/%20/g, ' ');
        }
    }, [encodedCity, cityOverride]);

    const updatePubInArray = (pubsArray: PubObject[], pubId: string, updatedData: Partial<PubObject>): PubObject[] => {
        const index = pubsArray.findIndex(p => p.google_place_id === pubId);
        if (index === -1) return pubsArray;
        const newArray = [...pubsArray];
        newArray[index] = { ...newArray[index], ...updatedData };
        return newArray;
    };

    const fetchPubRaycasting = useCallback(async (pub: PubObject) => {
        try {
            const response = await getPubRaycasting(pub.google_place_id, false, false);
            const pubApiData = response.data as any;
            const dataToUpdate: Partial<PubObject> = {
                has_no_garden: pubApiData.has_no_garden || pubApiData.status === 'no_garden',
                sunrise: pubApiData.sunrise,
                sunset: pubApiData.sunset,
                coverage_quality: pubApiData.coverage_quality,
            };

            if (!dataToUpdate.has_no_garden && pubApiData.garden_details) {
                const gardenDetails = pubApiData.garden_details;
                const gardenDetailKeys = Object.keys(gardenDetails);
                const heatmapKeys = Object.keys(pubApiData).filter(key =>
                    typeof pubApiData[key] === 'object' && pubApiData[key] !== null && (pubApiData[key] as any).heatmap_1d &&
                    !['pub_id', 'date', 'original_query_date', 'status', 'garden_details', 'has_no_garden', 'mesh_uris', 'centre_ecef', 'copyrights', 'sun_vector', 'sunrise', 'sunset', 'session_id'].includes(key)
                );
                const allGardenIds = Array.from(new Set([...gardenDetailKeys, ...heatmapKeys]));
                const processedGardens = allGardenIds
                    .filter(id => pubApiData[id] && (pubApiData[id] as any).heatmap_1d)
                    .map(subAreaId => {
                        const gardenApiData = pubApiData[subAreaId] as any;
                        return {
                            name: gardenDetails[subAreaId]?.name || `Garden ${subAreaId.substring(0,8)}`,
                            uid: subAreaId,
                            heatmap: gardenApiData.heatmap_1d,
                            is_uncertain: gardenDetails[subAreaId]?.is_uncertain || false,
                            garden_size: gardenDetails[subAreaId]?.garden_size,
                        };
                    });
                dataToUpdate.gardens = processedGardens;
                let totalSunPoints = 0;
                let gardensWithSunData = 0;
                processedGardens.forEach(g => {
                    const gardenApiData = pubApiData[g.uid] as any;
                    if (gardenApiData?.total_sun_points) { totalSunPoints += gardenApiData.total_sun_points; gardensWithSunData++; }
                });
                if (gardensWithSunData > 0) dataToUpdate.rating = calculateBeerMugRating(totalSunPoints / gardensWithSunData);
                if (processedGardens.length > 0 && processedGardens[0].heatmap) dataToUpdate.heatmap = processedGardens[0].heatmap;
                else if (pubApiData.heatmap_1d && typeof pubApiData.heatmap_1d !== 'string') dataToUpdate.heatmap = pubApiData.heatmap_1d as HeatmapData;
            }
            setSunniestPubs(prev => updatePubInArray(prev, pub.google_place_id, dataToUpdate));
        } catch (error) {
            console.error(`Error fetching raycasting data for pub ${pub.google_place_id}:`, error);
            setSunniestPubs(prev => updatePubInArray(prev, pub.google_place_id, { heatmap: undefined }));
        } finally {
            setRaycastingPubIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(pub.google_place_id);
                if (newSet.size === 0) {
                    setRaycastLoading(false);
                }
                return newSet;
            });
        }
    }, []);
    
    // Always scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    
    // Also scroll to top when city changes
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [encodedCity, cityOverride]);
    
    useEffect(() => {
        const cityToFetch = getDecodedCityName();
        if (!cityToFetch) return;

        const fetchCityData = async () => {
            setLoading(true);
            setErrorMessage(null);
            setCityName(cityToFetch);
            
            try {
                const apiCall = isEnhancedPage 
                    ? getSunnyPubsForEnhancedCity(cityToFetch, pubCount)
                    : getSunnyPubsByCity(cityToFetch, pubCount);

                const response = await apiCall;
                
                if (response.data && response.data.pubs && response.data.pubs.length > 0) {
                    setTotalPubsInCity(response.data.total_pubs_in_city || 0);
                    
                    const transformedPubs: PubObject[] = response.data.pubs.map((apiPub: any) => {
                        let distance = 0;
                        if (searchLocation && apiPub.latitude && apiPub.longitude) {
                            distance = metresBetween(
                                searchLocation.latitude,
                                searchLocation.longitude,
                                apiPub.latitude,
                                apiPub.longitude
                            );
                        }

                        return {
                            name: apiPub.name || 'Unknown Pub',
                            distance: distance,
                            google_place_id: apiPub.google_place_id,
                            address: apiPub.address || '',
                            city: cityToFetch,
                            latitude: apiPub.latitude || 0,
                            longitude: apiPub.longitude || 0,
                            garden_drawn: true,
                            garden_moderated: false,
                            has_no_garden: false,
                            gardens: apiPub.gardens ? apiPub.gardens.map((garden: any) => ({
                                name: garden.name || 'Garden',
                                uid: garden.uid,
                                is_uncertain: garden.is_uncertain || false,
                                garden_size: garden.garden_size
                            })) : [],
                            rating: undefined,
                            heatmap: undefined,
                            sunrise: undefined,
                            sunset: undefined,
                            coverage_quality: undefined
                        };
                    });
                    
                    setSunniestPubs(transformedPubs);
                    
                    const pubsToQuery = transformedPubs.filter(p => !p.has_no_garden);
                    if (pubsToQuery.length > 0) {
                        setRaycastingPubIds(new Set(pubsToQuery.map(p => p.google_place_id)));
                        setRaycastLoading(true);
                        
                        Promise.allSettled(pubsToQuery.map(p => fetchPubRaycasting(p)))
                            .catch(error => {
                                console.error("Error processing batch of pub raycasting calls:", error);
                            });
                    } else {
                        // Ensure raycastLoading is set to false if there's nothing to query
                        setRaycastLoading(false);
                    }
                } else {
                    setErrorMessage(`No pubs with garden sun data found in ${cityToFetch}.`);
                    setSunniestPubs([]);
                    setTotalPubsInCity(0);
                    setRaycastLoading(false); // No pubs, so no raycasting
                }
            } catch (error) {
                console.error('Error fetching city data:', error);
                setErrorMessage('Unable to load pub data for this city. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        
        fetchCityData();
    }, [getDecodedCityName, isEnhancedPage, pubCount, fetchPubRaycasting, searchLocation]);
    
    const debouncedSearch = useCallback(
        debounce((query: string) => {
            if (!query || query.length < 2) {
                setCitySuggestions([]);
                setSearchLoading(false);
                return;
            }
            setSearchLoading(true);
            searchCities(query)
                .then(response => setCitySuggestions(response.data?.cities || []))
                .catch(err => {
                    console.error('Error searching cities:', err);
                    setCitySuggestions([]);
                })
                .finally(() => setSearchLoading(false));
        }, 300),
        []
    );

    useEffect(() => { debouncedSearch(searchQuery); }, [searchQuery, debouncedSearch]);
    
    // --- CHANGE: The page refresh hack has been removed. ---

    const handleCitySelect = (city: City | null) => {
        if (city) {
            setSelectedCity(city);
            const encodedCityName = encodeURIComponent(city.city);
            navigate(`/sunny-pubs-in/${encodedCityName}`);
        }
    };
    
    const handleViewPub = (pub: PubObject) => {
        localStorage.setItem('currentViewPubGPId', pub.google_place_id);
        navigate(`/pub/${pub.google_place_id}`, { state: { pubToView: pub } });
    };

    const handleSubmitGarden = (pub: PubObject) => {
        navigate(`/submit?pub_id=${pub.google_place_id}`, { state: { selectedPub: pub }});
    };
    
    useEffect(() => {
        const baseUrl = window.location.origin;
        const path = window.location.pathname;
        setCanonicalUrl(`${baseUrl}${path}`);
    }, []);
    
    const generateSeoMetadata = () => {
        const pageTitleName = enhancedDisplayName || cityName;
        const topPubName = sunniestPubs.length > 0 ? sunniestPubs[0].name : '';
        const currentPubCount = loading ? pubCount : sunniestPubs.length;
        
        return {
            title: `Top ${currentPubCount} Sunniest Pub Gardens in ${pageTitleName}`,
            description: `Find ${pageTitleName}'s sunniest pub gardens, ranked by our live sun-tracking. Discover the best beer gardens for sunshine, including ${topPubName}.`,
            keywords: `sunny pubs, ${pageTitleName} pubs, beer gardens, outdoor pubs, sunshine ranking, ${pageTitleName} beer gardens, pub gardens with sun, ${topPubName || 'best pubs'}, ${pageTitleName} sunny spots`
        };
    };
    
    const seoData = generateSeoMetadata();
    
    const navigateToPubNearMe = () => {
        clearCustomSearchLocation();
        navigate("/pubs");
    }

    const getLocationAndNavigate = async () => {
        const location = await requestUserLocation();
        if (location) {
            navigateToPubNearMe();
        }
    };
    
    // --- CHANGE: pubListContent is simplified. The loading state is handled outside of it. ---
    const pubListContent = (
        <>
            {errorMessage && (
                <StyledPaper elevation={1} sx={{ bgcolor: 'rgba(255, 235, 235, 0.5)', border: '1px solid rgba(255, 0, 0, 0.1)', textAlign: 'center', py: 4, width: '100%', maxWidth: 800 }}>
                    <Typography color="error" variant="h6" sx={{ mb: 2 }}>{errorMessage}</Typography>
                    <Typography variant="body1">
                        Try searching for another city or visit our <MuiLink href="/" underline="hover">home page</MuiLink>.
                    </Typography>
                </StyledPaper>
            )}
            
            {!errorMessage && sunniestPubs.length > 0 && (
                 <Grid container spacing={0} sx={{ width: '100%', maxWidth: 800, mt: 4 }}>
                    {sunniestPubs.map((pub, index) => (
                        <React.Fragment key={pub.google_place_id}>
                            <Grid item xs={12} sx={{ mb: 4 }}>
                                <Typography variant="h4" component="div" sx={{ fontFamily: "ApfelGrotezkBold", fontWeight: '700', mb: 1.5, color: themeColors.darkBackground }}>
                                    {index + 1}.
                                </Typography>
                                <PubCard
                                    pub={pub}
                                    timelineMode={isMobile ? 'compact' : 'full'}
                                    raycastLoadingForThisPub={raycastingPubIds.has(pub.google_place_id)} // This prop remains useful for individual card states if needed
                                    onViewPub={handleViewPub}
                                    onSubmitGarden={handleSubmitGarden}
                                    isBot={isBot}
                                />
                            </Grid>
                            {/* Show in-feed ad after every 3rd pub (indices 2, 5, 8, etc.) */}
                            {(index + 1) % 3 === 0 && !isBot && (
                                <Grid item xs={12} sx={{ mb: 4 }}>
                                    <AdSense 
                                        client="ca-pub-3335995580070945" 
                                        slot="1820372142" 
                                        format="fluid" 
                                        layoutKey="-7g+eh+16-4o+6v"
                                        width="100%" 
                                    />
                                </Grid>
                            )}
                        </React.Fragment>
                    ))}
                </Grid>
            )}
        </>
    );

    return (
        <Box sx={{ width: '100%', minHeight: '100vh' }}>
            <Fonts />
            <Helmet>
                <title>{seoData.title}</title>
                <meta name="description" content={seoData.description} />
                <meta name="keywords" content={seoData.keywords} />
                <link rel="canonical" href={canonicalUrl} />
                
                <meta property="og:title" content={seoData.title} />
                <meta property="og:description" content={seoData.description} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content="https://sun-tap.com/banner-1200-630.png" />
                
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoData.title} />
                <meta name="twitter:description" content={seoData.description} />
                <meta name="twitter:image" content="https://sun-tap.com/banner-1200-630.png" />
                
                {!isFullyLoading && sunniestPubs.length > 0 && (
                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "ItemList",
                            "name": seoData.title,
                            "description": seoData.description,
                            "url": canonicalUrl,
                            "numberOfItems": sunniestPubs.length,
                            "itemListElement": sunniestPubs.slice(0, 5).map((pub, index) => ({
                                "@type": "ListItem",
                                "position": index + 1,
                                "item": {
                                    "@type": "Restaurant",
                                    "name": pub.name,
                                    "address": pub.address,
                                    "geo": {
                                        "@type": "GeoCoordinates",
                                        "latitude": pub.latitude,
                                        "longitude": pub.longitude
                                    }
                                }
                            }))
                        })}
                    </script>
                )}
            </Helmet>
            
            <TopNav title={`Sunny Pubs in ${enhancedDisplayName || cityName}`} />
            
            <Container maxWidth="lg" sx={{ pt: '16px', pb: 4, px: '5%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', textAlign: 'left', mb: 3 }}>
                    <StyledHeading component="h1" sx={{ fontSize: isMobile ? '4rem' : '6rem', mb: 1, textTransform: 'capitalize' }}>
                        {enhancedDisplayName || cityName}
                    </StyledHeading>
                    <Typography variant="h4" component="h2" sx={{ color: 'black', fontWeight: 700, fontSize: isMobile ? '1.8rem' : '2.5rem', mb: 2, fontFamily: "ApfelGrotezkBold" }}>
                        Top {loading ? pubCount : sunniestPubs.length} Sunniest Pubs
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#555', fontSize: '1.1rem', maxWidth: '600px', mb: enhancedBlurb ? 2 : 0, lineHeight: 1.4, fontFamily: "ApfelGrotezkRegular" }}>
                        Find {(enhancedDisplayName || cityName)}'s sunniest pub gardens, ranked by our live sun-tracking.
                        {!loading && totalPubsInCity > 0 && (
                            <> We currently have {totalPubsInCity} pub{totalPubsInCity !== 1 ? 's' : ''} mapped in {enhancedDisplayName || cityName}.</>
                        )}
                    </Typography>
                    {enhancedBlurb && (
                        <Typography variant="body1" sx={{ color: '#555', fontSize: '1.1rem', maxWidth: '600px', lineHeight: 1.4, fontFamily: "ApfelGrotezkRegular" }}>
                            {enhancedBlurb}
                        </Typography>
                    )}
                </Box>

                {/* --- CHANGE: Main content area now uses the combined loading state --- */}
                {isFullyLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, minHeight: '50vh' }}>
                        <CircularProgress sx={{ color: themeColors.orange, mb: 2 }} />
                        <Typography variant="body1" sx={{ color: '#666' }}>
                            Finding the sunniest spots in {enhancedDisplayName || cityName}...
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {pubListContent}

                        <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3, mt: 4 }}>
                            <StyledPaper elevation={2}>
                                <Typography variant="h6" component="h3" sx={{ mb: 2, fontFamily: "ApfelGrotezkRegular", color: themeColors.darkBackground, fontWeight: 600 }}>
                                    Explore Other Cities' Top {pubCount} Sunniest Pubs
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 1.5 }}>
                                    <Autocomplete fullWidth freeSolo loading={searchLoading} options={citySuggestions}
                                        getOptionLabel={(option) => typeof option === 'string' ? option : option.city}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props} key={option.city} sx={{ py: 1, display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}><LocationOn sx={{ color: themeColors.blue, mr: 1 }} /> <Typography variant="body1">{option.city}</Typography></Box>
                                                <Chip size="small" label={`${option.verified_gardens} gardens`} sx={{ bgcolor: 'rgba(60, 104, 169, 0.1)', color: themeColors.blue, fontSize: '0.7rem', fontWeight: 500 }} />
                                            </Box>
                                        )}
                                        onChange={(_, value) => { if (value && typeof value !== 'string') { handleCitySelect(value); } }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Search cities" variant="outlined" onChange={(e) => setSearchQuery(e.target.value)} placeholder="Type a city name..."
                                                InputProps={{ ...params.InputProps, startAdornment: (<Search color="action" sx={{ ml: 1, mr: 0.5 }} />), endAdornment: (<>{searchLoading ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>),
                                                    sx: { borderRadius: '12px' }
                                                }}
                                            />
                                        )}
                                    />
                                    <SearchButton variant="contained" disabled={!selectedCity} onClick={() => handleCitySelect(selectedCity)} sx={{ minWidth: { xs: '100%', sm: '140px' }, height: { xs: 'auto', sm: '56px' } }}>
                                        Search
                                    </SearchButton>
                                </Box>
                            </StyledPaper>

                            <Paper
                                elevation={2}
                                sx={{ p: 3, borderRadius: '24px', background: themeColors.lightYellow, backdropFilter: 'blur(16px)', cursor: 'pointer' }}
                                onClick={() => {
                                    if (hasLocationPermission) {
                                        navigateToPubNearMe();
                                    } else {
                                        setLocationModalOpen(true);
                                    }
                                }}
                            >
                                <Typography component="h3" sx={{ mb: 3, fontFamily: "ApfelGrotezkRegular", fontSize: '30px', fontWeight: '600', lineHeight: '1.2' }}>
                                    Find sunny pubs near you
                                </Typography>
                                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                    <PrimaryButton
                                        sx={{ padding: '4px', width: '100%', minWidth: '180px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: '24px' }}
                                        endIcon={<LocationOnIcon />}
                                    >
                                        Find a sunny pub
                                    </PrimaryButton>
                                </Stack>
                            </Paper>

                            <Paper
                                elevation={2}
                                sx={{ p: 3, borderRadius: '24px', background: themeColors.lightYellow, backdropFilter: 'blur(16px)', cursor: 'pointer' }}
                                onClick={() => navigate('/search-area')}
                            >
                                <Typography sx={{ mb: 10, fontFamily: "ApfelGrotezkRegular", fontSize: '30px', fontWeight: '600', lineHeight: '1.2' }}>
                                    Search an area and add your favourite pubs
                                </Typography>
                                    <PrimaryButton
                                        sx={{ padding: '4px', width: '100%', minWidth: '180px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: '24px' }}
                                        endIcon={<MapIcon />}
                                    >
                                        Explore the map
                                    </PrimaryButton>
                            </Paper>

                            <Paper
                                elevation={2}
                                sx={{ position: 'relative', p: 3, borderRadius: '24px', background: themeColors.lightYellow, backdropFilter: 'blur(16px)', height: '440px' }}
                            >
                                <Typography sx={{ mb: 1, fontFamily: "ApfelGrotezkRegular", fontSize: '30px', fontWeight: '600', lineHeight: '1.2' }}>
                                    About us
                                </Typography>
                                <Typography sx={{ fontFamily: "ApfelGrotezkRegular", fontSize: '20px', fontWeight: '400', lineHeight: '1.2' }}>
                                    Join our community of pub enthusiasts and help us build the most accurate guide to the UK's sunniest pub gardens.
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '240px', position: 'absolute', bottom: '10px', right: '10px' }} >
                                    <img src={pintManIllustration} alt="Man holding a pint" height="100%" />
                                </Box>
                                <PrimaryButton
                                    sx={{ padding: '4px', width: '60%', minWidth: '180px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: '24px', position: 'absolute', bottom: '20px' }}
                                    onClick={() => navigate("/about")}
                                    endIcon={<ArrowForward />}
                                >
                                    Find out more
                                </PrimaryButton>
                            </Paper>
                        </Box>
                    </>
                )}
            </Container>
            
            <Container maxWidth="lg" sx={{ mt: 4, px: '5%' }}>
                <Footer />
            </Container>
            
            <ScrollIndicator forceVisible={true} iconColor={themeColors.orange} backgroundColor="rgba(255, 255, 255, 0.8)" />
            <Dialog
                open={locationModalOpen}
                onClose={() => setLocationModalOpen(false)}
                PaperProps={{ sx: { borderRadius: 3, maxWidth: 500, px: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#222', pb: 1, pt: 3 }}>
                    Location Permission
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: 'text.secondary' }}>
                        We need your location to find sunny pubs near you. Your location data is only used to calculate distances and will not be stored.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} width="100%" alignItems="center">
                        <PrimaryButton
                            onClick={() => setLocationModalOpen(false)}
                            sx={{ fontWeight: 500, padding: '4px', width: { xs: '100%', sm: '40%' }, minWidth: { xs: '100%', sm: '180px' } }}
                        >
                            Cancel
                        </PrimaryButton>
                        <PrimaryButton
                            onClick={() => { setLocationModalOpen(false); getLocationAndNavigate(); }}
                            sx={{ fontWeight: 500, padding: '4px', width: { xs: '100%', sm: '40%' }, minWidth: { xs: '100%', sm: '180px' }, '&:hover': { backgroundColor: themeColors.mainYellow, } }}
                        >
                            Allow
                        </PrimaryButton>
                    </Stack>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CityLandingPage;